import React, { useState } from "react";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Rating from "@mui/material/Rating";
import Box from "@mui/material/Box";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import StarIcon from "@mui/icons-material/Star";
import CircularProgress from "@mui/material/CircularProgress";
import { CardBody, CardHeader, CardTitle } from "react-bootstrap";
import { useHistory } from "react-router-dom";

import ContentWrapper from "../../components/common/ContentWrapper";
import Card from "../../components/common/Card";

import {
  ExamFeedbackProvider,
  useExamFeedback,
} from "../../context/Exam/ExamFeedbackContext";

import "../assessment/components/feedbacks/FeedbackAssessment.css";

function FeedbackAssessmentBody({ onClose, assessmentID }) {
  const history = useHistory();
  const [showThankyouPage, setThankyouPage] = useState(false);
    const [showFeedbackForm, setShowFeedbackForm] = useState(false);

    const {
        templateData,
        summary,
        answers,
        loading,
        submitLoading,
        error,
        calculateDuration,
        isOtherOption,
        getCategoryById,
        getOptionsByQuestionId,
        handleAnswerChange,
        handleCheckboxChange,
        submitFeedback,
    } = useExamFeedback();

    const handleSubmit = async () => {
        const success = await submitFeedback();
        if (success) {
      setThankyouPage(true);
      setShowFeedbackForm(false);
    } else {
      alert("Failed to save feedback.");
    }
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <ContentWrapper>
        <Card>
          <CardBody className="p-6">
            <p className="text-danger">{error}</p>
            <Button variant="contained" onClick={() => onClose?.({ action: "error" })}>
              Continue
            </Button>
          </CardBody>
        </Card>
      </ContentWrapper>
    );
  }

  if (!templateData) return null;

  return (
    <div style={{ height: "90vh", overflow: "auto" }}>
      <ContentWrapper>
        {submitLoading && (
          <div className="loading-overlay">
            <Box sx={{ display: "flex" }}>
              <CircularProgress />
            </Box>
          </div>
        )}

        <Card>
          {!showFeedbackForm && !showThankyouPage && (
            <CardBody className="p-6">
              <CardHeader className="mb-4">
                <CardTitle cardLabel="Assessment Report" />

                <div className="rounded-xl border border-green-200 bg-gradient-to-r from-green-50 to-green-100 p-5">
                  <h2 className="text-lg font-bold text-green-800 flex items-center gap-2">
                    🎓 Congratulations!
                  </h2>

                  <p className="mt-2 text-green-900">
                    You have successfully completed the
                    <span className="font-semibold text-green-700">
                      "{summary.assessment_title || assessmentID}".
                    </span>
                  </p>

                  <div className="mt-3 rounded-lg bg-white/70 p-3 text-sm text-gray-700">
                    Below is a summary of your exam performance.
                  </div>
                </div>
              </CardHeader>

              <div className="overflow-hidden rounded-lg border">
                <table className="w-full text-sm">
                  <tbody className="divide-y">
                    <tr>
                      <td className="summary-label">Assessment ID</td>
                      <td className="summary-value">{assessmentID || "N/A"}</td>
                    </tr>
                    <tr>
                      <td className="summary-label">Assessment Title</td>
                      <td className="summary-value">{summary.assessment_title || "N/A"}</td>
                    </tr>
                    <tr>
                      <td className="summary-label">Subject</td>
                      <td className="summary-value">{summary.subject || "N/A"}</td>
                    </tr>
                    <tr>
                      <td className="summary-label">Total Questions</td>
                      <td className="summary-value">{summary.questionCount ?? "N/A"}</td>
                    </tr>
                    <tr>
                      <td className="summary-label">Answered Questions</td>
                      <td className="summary-value">{summary.answeredCount ?? "N/A"}</td>
                    </tr>
                    <tr>
                      <td className="summary-label">Exam Start Time</td>
                      <td className="summary-value">{summary.examStartTime || "N/A"}</td>
                    </tr>
                    <tr>
                      <td className="summary-label">Exam Finish Time</td>
                      <td className="summary-value">{summary.examFinishTime || "N/A"}</td>
                    </tr>
                    <tr>
                      <td className="summary-label">Assigned Duration</td>
                      <td className="summary-value">
                        {summary.time_duration ? `${summary.time_duration} min` : "Unlimited"}
                      </td>
                    </tr>
                    <tr>
                      <td className="summary-label">Time Spent</td>
                      <td className="summary-value font-semibold">
                        {calculateDuration(summary.examStartTime, summary.examFinishTime)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4">
                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                  💡 Help us improve!
                </h3>
                <p className="mt-1 text-sm text-blue-700">
                  Please complete a short feedback form.
                  <span className="font-medium"> Your input is valuable.</span>
                </p>
              </div>

              <div className="mt-4 flex justify-end">
                <Button variant="contained" color="primary" onClick={() => setShowFeedbackForm(true)}>
                  Next
                </Button>
              </div>
            </CardBody>
          )}

          {showFeedbackForm && !showThankyouPage && (
            <CardBody>
              <div className="modal-title-custom">Student-Friendly Questionnaire</div>
              <div className="template-title">{templateData.title}</div>
              <div className="template-title2">{templateData.description}</div>
              <div className="template-title3">ASMNT{assessmentID}</div>
              <div className="template-title4">"{summary.assessment_title}"</div>

              {templateData.sections.map((section) => (
                <div key={section.fb_sec_id} className="feedback-section">
                  <h5 className="section-title">{section.title}</h5>

                  {section.allow_instruction && (
                    <p className="section-instruction">{section.instruction}</p>
                  )}

                  {section.questions.map((question) => {
                    const category = getCategoryById(question.fb_qus_cat_id);
                    const options = getOptionsByQuestionId(question.qus_id);

                    return (
                      <div key={question.qus_id} className="feedback-question">
                        <p className="question-content">{question.qus_content}</p>

                        {category === "text" && (
                          <TextField
                            fullWidth
                            variant="outlined"
                            value={answers[question.qus_id] || ""}
                            onChange={(e) =>
                              handleAnswerChange(question.qus_id, e.target.value)
                            }
                          />
                        )}

                        {category === "rating" && (
                          <Box className="rating-box">
                            <Rating
                              name={`rating-${question.qus_id}`}
                              value={answers[question.qus_id] || 0}
                              onChange={(event, newValue) =>
                                handleAnswerChange(question.qus_id, newValue)
                              }
                              precision={1}
                              emptyIcon={
                                <StarIcon style={{ opacity: 0.3 }} fontSize="inherit" />
                              }
                            />
                          </Box>
                        )}

                        {(category === "choice_single" || category === "yes_no") && (
                          <div>
                            <RadioGroup
                              value={answers[question.qus_id]?.selected || ""}
                              onChange={(e) =>
                                handleAnswerChange(question.qus_id, {
                                  selected: parseInt(e.target.value),
                                  otherText: "",
                                })
                              }
                            >
                              {options.map((option) => (
                                <FormControlLabel
                                  key={option.fb_qus_ans_opt_id}
                                  value={option.fb_qus_ans_opt_id}
                                  control={<Radio />}
                                  label={option.answer_option}
                                />
                              ))}
                            </RadioGroup>

                            {answers[question.qus_id]?.selected &&
                              isOtherOption(
                                options.find(
                                  (option) =>
                                    option.fb_qus_ans_opt_id ===
                                    answers[question.qus_id]?.selected
                                )?.answer_option
                              ) && (
                                <TextField
                                  sx={{ width: "60%", mt: 1 }}
                                  variant="outlined"
                                  placeholder="Please specify..."
                                  value={answers[question.qus_id]?.otherText || ""}
                                  onChange={(e) =>
                                    handleAnswerChange(question.qus_id, {
                                      ...answers[question.qus_id],
                                      otherText: e.target.value,
                                    })
                                  }
                                />
                              )}
                          </div>
                        )}

                        {category === "choice_multi" && (
                          <div className="checkbox-group">
                            {options.map((option) => (
                              <FormControlLabel
                                key={option.fb_qus_ans_opt_id}
                                control={
                                  <Checkbox
                                    checked={(answers[question.qus_id]?.selected || []).includes(
                                      option.fb_qus_ans_opt_id
                                    )}
                                    onChange={() =>
                                      handleCheckboxChange(
                                        question.qus_id,
                                        option.fb_qus_ans_opt_id
                                      )
                                    }
                                  />
                                }
                                label={option.answer_option}
                              />
                            ))}

                            {(answers[question.qus_id]?.selected || []).map((id) => {
                              const option = options.find(
                                (item) => item.fb_qus_ans_opt_id === id
                              );

                              if (!option || !isOtherOption(option.answer_option)) return null;

                              return (
                                <TextField
                                  key={`other-${id}`}
                                  sx={{ width: "60%", mt: 1 }}
                                  variant="outlined"
                                  placeholder="Please specify..."
                                  value={answers[question.qus_id]?.otherText || ""}
                                  onChange={(e) =>
                                    handleAnswerChange(question.qus_id, {
                                      ...answers[question.qus_id],
                                      otherText: e.target.value,
                                    })
                                  }
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}

              <Stack spacing={2} direction="row" mt={2}>
                <Button
                  variant="outlined"
                  onClick={() => setShowFeedbackForm(false)}
                  disabled={submitLoading}
                >
                  Cancel
                </Button>

                <Button variant="contained" onClick={handleSubmit} disabled={submitLoading}>
                  {submitLoading ? <CircularProgress size={24} /> : "Submit Feedback"}
                </Button>
              </Stack>
            </CardBody>
          )}

          {!showFeedbackForm && showThankyouPage && (
            <div className="flex min-h-[60vh] items-center justify-center px-4">
              <div className="w-full max-w-md rounded-2xl border bg-white p-8 text-center shadow-sm">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl">
                  🎉
                </div>

                <h2 className="text-xl font-bold text-gray-900">Thank You!</h2>

                <p className="mt-2 text-sm text-gray-600">
                  You have successfully completed the assessment and submitted your feedback.
                </p>

                <p className="mt-3 text-sm text-gray-700">
                  🌟 We truly appreciate your time and effort.
                  <br />
                  Best wishes for your results and future success!
                </p>

                <div className="mt-6 flex gap-3 justify-center">
                  <Button variant="outlined" color="primary" onClick={() => history.goBack()}>
                    Go to Dashboard
                  </Button>

                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => onClose?.({ action: "success" })}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </ContentWrapper>
    </div>
  );
}

function ExamFeedback({ onClose, assessmentID, candidate_id, examSummary }) {
  return (
    <ExamFeedbackProvider
      assessmentID={assessmentID}
      candidate_id={candidate_id}
      examSummary={examSummary}
    >
      <FeedbackAssessmentBody onClose={onClose} assessmentID={assessmentID} />
    </ExamFeedbackProvider>
  );
}

export default ExamFeedback;